import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Check, Crown, Sparkles, Heart, Target, BookOpen, MessageCircle, Wind, Calendar, User, Users, Shield, Mail, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { user, token, getAuthHeader, isTrial, trialDaysLeft, isPremium } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Referral code state (from ?code= or localStorage)
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);

  // LIVON offer (from email link ?erbjudande=livon) — auto-applies 100 kr discount
  const [livonOffer, setLivonOffer] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();

    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }

    // Read LIVON offer from URL or localStorage
    const urlOffer = searchParams.get('erbjudande');
    if (urlOffer === 'livon') {
      localStorage.setItem('livon_offer', 'livon');
      setLivonOffer(true);
    } else if (localStorage.getItem('livon_offer') === 'livon') {
      setLivonOffer(true);
    }

    // Read referral code from URL or localStorage
    const urlCode = searchParams.get('code');
    const storedCode = localStorage.getItem('livon_referral_code');
    const code = urlCode || storedCode;
    if (urlCode) {
      localStorage.setItem('livon_referral_code', urlCode);
    }
    if (code) {
      setReferralCode(code);
      validateReferralCode(code);
    }
  }, []);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/referral/validate/${encodeURIComponent(code)}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
      } else {
        setReferralInfo({ valid: false });
        localStorage.removeItem('livon_referral_code');
      }
    } catch (e) {
      setReferralInfo({ valid: false });
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader()
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Could not fetch subscription status');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Kunde inte verifiera betalningen. Kontakta support om problemet kvarstår.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: getAuthHeader()
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Betalningen lyckades! Välkommen till Premium!');
        setCheckingPayment(false);
        fetchSubscriptionStatus();
        // Clear URL params
        navigate('/subscription', { replace: true });
        // Reload to update user context
        window.location.reload();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Betalningssessionen har gått ut. Försök igen.');
        setCheckingPayment(false);
        navigate('/subscription', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin
      };
      if (referralInfo?.valid && referralCode) {
        payload.referral_code = referralCode;
      } else if (livonOffer) {
        payload.apply_livon_discount = true;
      }
      const response = await axios.post(`${API}/subscription/checkout`, payload, {
        headers: getAuthHeader()
      });

      if (response.data.url) {
        // Clear stored referral code — it's now being applied in Stripe
        localStorage.removeItem('livon_referral_code');
        localStorage.removeItem('livon_offer');
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Kunde inte starta betalningen. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const premiumFeatures = [
    { icon: BookOpen, title: 'Dagbok', description: 'Obegränsade anteckningar och reflektioner' },
    { icon: Calendar, title: 'Guidade reflektioner', description: 'Morgon, kväll, vecka och månadsreflektioner' },
    { icon: Heart, title: 'Känslor & Tacksamhet', description: 'Verktyg för att förstå och hantera känslor' },
    { icon: Wind, title: 'Andning & Meditation', description: '15+ övningar för lugn och avslappning' },
    { icon: Target, title: 'Mål & Vanor', description: 'Sätt mål och bygg goda vanor' },
    { icon: User, title: 'Självkännedom', description: 'Djupa frågor för personlig utveckling' },
    { icon: Users, title: 'Relationer & Gränser', description: 'Verktyg för hälsosamma relationer' },
    { icon: Clock, title: 'Städschema & Rutiner', description: 'Håll koll på hemmet och vardagen' },
    { icon: Sparkles, title: 'Anteckningar', description: 'Organisera tankar och idéer' },
    { icon: Shield, title: 'Allt i en app', description: 'Hälsa, hem, planering och ekonomi' }
  ];

  const freeFeatures = [
    { icon: BookOpen, title: '3 dagars fri provperiod', description: 'Upplev alla funktioner utan kostnad' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka
          </Button>
          <h1 className="font-heading text-xl font-light">Premium</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Payment checking overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <p className="text-lg">Verifierar betalning...</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Trial banner */}
        {isTrial && !isPremium && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8 animate-fade-in">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-amber-800">Provperiod aktiv</h3>
                <p className="text-amber-700">
                  Du har <strong>{trialDaysLeft} {trialDaysLeft === 1 ? 'dag' : 'dagar'}</strong> kvar att utforska alla funktioner gratis.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Current status */}
        {isPremium ? (
          <div className="bg-gradient-to-br from-primary/20 to-accent/30 rounded-2xl p-8 mb-8 text-center animate-fade-in">
            <Crown className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-2xl mb-2">Du har full tillgång!</h2>
            <p className="text-muted-foreground">
              Du har åtkomst till alla funktioner — använd dem när du vill.
            </p>
          </div>
        ) : (
          <div className="text-center mb-12 space-y-4 animate-fade-in">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-3xl font-light">
              {isTrial ? 'Fortsätt efter provperioden' : 'Lås upp hela Livon'}
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {isTrial 
                ? 'Behåll tillgång till alla funktioner för ditt välmående — köp en gång, använd när du vill.'
                : 'Köp en gång och få full tillgång till alla verktyg för ditt välmående.'
              }
            </p>
          </div>
        )}

        {/* Premium features */}
        <div className="space-y-4 mb-8">
          <h3 className="font-heading text-xl">Premium inkluderar</h3>
          <div className="grid gap-3">
            {premiumFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-primary/5 opacity-0 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
                <Check className="w-5 h-5 text-primary ml-auto shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Free features */}
        <div className="space-y-4 mb-8">
          <h3 className="font-heading text-xl text-muted-foreground">Gratis (ingår alltid)</h3>
          <div className="grid gap-3">
            {freeFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-secondary/30"
              >
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <feature.icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h4 className="font-medium text-muted-foreground">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
                <Check className="w-5 h-5 text-muted-foreground ml-auto shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        {!isPremium && (
          <div className="space-y-4 animate-fade-in">
            {/* Referral discount banner */}
            {referralInfo?.valid && (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5 flex items-center gap-3" data-testid="referral-banner">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                  <Heart className="w-5 h-5 text-rose-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-rose-900">
                    {referralInfo.referrer_name} har bjudit in dig!
                  </p>
                  <p className="text-sm text-rose-700">
                    Du får {referralInfo.discount_amount} kr rabatt — {referralInfo.final_price} kr istället för {referralInfo.original_price} kr.
                  </p>
                </div>
              </div>
            )}

            {/* LIVON offer banner (from email link) */}
            {livonOffer && !referralInfo?.valid && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-3" data-testid="livon-offer-banner">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-900">
                    Ditt erbjudande är aktiverat
                  </p>
                  <p className="text-sm text-amber-700">
                    100 kr rabatt dras av automatiskt i kassan — du betalar 199 kr istället för 299 kr.
                  </p>
                </div>
              </div>
            )}

            {/* Lifetime One-Time Payment */}
            <div className="bg-card rounded-2xl p-8 border-2 border-primary relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                Engångsbetalning — full tillgång
              </div>
              <div className="text-center mb-6 pt-2">
                {referralInfo?.valid ? (
                  <>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="font-heading text-5xl font-semibold text-rose-600" data-testid="discounted-price">
                        {referralInfo.final_price}
                      </span>
                      <span className="text-lg text-muted-foreground">kr</span>
                      <span className="text-xl text-muted-foreground line-through ml-2">
                        {referralInfo.original_price} kr
                      </span>
                    </div>
                    <p className="text-sm text-rose-700 mt-2">
                      Du sparar {referralInfo.discount_amount} kr tack vare {referralInfo.referrer_name}s kod.
                    </p>
                  </>
                ) : livonOffer ? (
                  <>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="font-heading text-5xl font-semibold text-amber-600" data-testid="livon-offer-price">199</span>
                      <span className="text-lg text-muted-foreground">kr</span>
                      <span className="text-xl text-muted-foreground line-through ml-2">299 kr</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-2">
                      Rabatten dras av automatiskt — ingen kod behövs.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-heading text-5xl font-semibold">299</span>
                      <span className="text-lg text-muted-foreground">kr</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Köp en gång — använd när du vill. Inga förnyelser, inga månadsavgifter.
                    </p>
                  </>
                )}
              </div>
              <Button
                onClick={() => handleSubscribe()}
                disabled={loading}
                className="w-full rounded-full h-12 text-base bg-primary hover:bg-primary/90"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    {referralInfo?.valid
                      ? `Köp Livon — ${referralInfo.final_price} kr`
                      : livonOffer
                        ? 'Behåll Livon — 199 kr'
                        : 'Köp Livon — 299 kr'}
                  </>
                )}
              </Button>
            </div>

            {/* Info */}
            <div className="bg-secondary/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground text-center">
                Säker betalning via Stripe. Engångsbetalning — inga återkommande kostnader.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
